import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ARTICLES, ARTICLE_SLUGS, type ArticleContent } from './data';

const DATE = '2026-08-26';

export default function ArticlePage() {
  const { lang, slug } = useParams();
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();

  const currentLang = lang && ['en', 'pt', 'de'].includes(lang) ? lang : 'en';
  const localized = ARTICLES[slug || ''] as { en?: ArticleContent; pt?: ArticleContent; de?: ArticleContent } | undefined;
  const content = localized?.[currentLang as 'en' | 'pt' | 'de'];

  useEffect(() => {
    i18n.changeLanguage(currentLang);
    window.scrollTo(0, 0);
  }, [currentLang, i18n]);

  if (!content) {
    return (
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <main className="pt-24 md:pt-28">
          <div className="max-w-2xl mx-auto px-4 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-3">Article not found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
            <a
              href={`/${currentLang}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              {t('article.breadcrumbHome')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const switchLanguage = (newLang: string) => {
    i18n.changeLanguage(newLang);
    navigate(`/${newLang}/articles/${slug}`);
  };

  const relatedSlugs = ARTICLE_SLUGS.filter((s) => s !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar />

      <main className="pt-24 md:pt-28">
        <article className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12" itemScope itemType="https://schema.org/Article">
          <meta itemProp="author" content={t('article.author')} />
          <meta itemProp="dateModified" content={DATE} />
          <meta itemProp="datePublished" content="2026-01-15" />

          {/* Breadcrumb */}
          <nav className="mb-6 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-foreground-500">
              <li><a href={`/${currentLang}`} className="hover:text-primary-500 transition-colors">{t('article.breadcrumbHome')}</a></li>
              <li><i className="ri-arrow-right-s-line text-xs" /></li>
              <li className="text-foreground-700">{content.cardTitle}</li>
            </ol>
          </nav>

          {/* Language Switcher */}
          <div className="flex items-center gap-2 mb-8">
            <span className="text-xs text-foreground-500">{currentLang.toUpperCase()} |</span>
            {(['en', 'pt', 'de'] as const).filter((l) => l !== currentLang).map((l) => (
              <button
                key={l}
                onClick={() => switchLanguage(l)}
                className="text-xs text-primary-600 hover:text-primary-700 underline transition-colors"
              >
                {l === 'pt' ? 'PT-BR' : l.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 font-heading mb-4 leading-tight" itemProp="headline">
            {content.h1}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {t('article.readTime', { minutes: content.readTime })}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {t('article.lastUpdated', { date: DATE })}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {t('article.author')}
            </span>
          </div>

          {/* Quick Answer */}
          <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 mb-10">
            <h2 className="font-semibold text-accent-800 mb-2 flex items-center gap-2">
              <i className="ri-flashlight-line" />
              {t('article.quickAnswer')}
            </h2>
            <p className="text-foreground-700 leading-relaxed">{content.quickAnswer}</p>
          </div>

          {/* Intro */}
          <p className="text-lg text-foreground-700 leading-relaxed mb-10">{content.intro}</p>

          {/* Sections */}
          {content.sections.map((section, i) => (
            <section key={i} className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-4">
                {section.h2}
              </h2>
              <p className="text-foreground-700 leading-relaxed">{section.content}</p>
            </section>
          ))}

          {/* Safety Note */}
          <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-6 mb-10">
            <h3 className="font-semibold text-foreground-800 mb-2 flex items-center gap-2">
              <i className="ri-shield-check-line text-accent-600" />
              Safety Note
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{t('article.safetyNote')}</p>
          </div>

          {/* Related Articles */}
          <div className="mb-10">
            <h3 className="font-semibold text-foreground-800 mb-4">{t('article.relatedArticles')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedSlugs.map((s) => {
                const rel = ARTICLES[s]?.[currentLang as 'en' | 'pt' | 'de'];
                if (!rel) return null;
                return (
                  <a
                    key={s}
                    href={`/${currentLang}/articles/${s}`}
                    className="block p-5 bg-background-100 rounded-xl border border-background-200 hover:border-primary-300 transition-all"
                  >
                    <h4 className="font-semibold text-foreground-800 mb-1 hover:text-primary-600 transition-colors text-sm">{rel.cardTitle}</h4>
                    <p className="text-xs text-foreground-500">{rel.cardDesc}</p>
                  </a>
                );
              })}
              <a href={`/${currentLang}`} className="block p-5 bg-primary-50 rounded-xl border border-primary-200 hover:border-primary-300 transition-all">
                <h4 className="font-semibold text-foreground-800 mb-1 hover:text-primary-600 transition-colors text-sm">CSSBuy Spreadsheet Home</h4>
                <p className="text-xs text-foreground-500">{t('final.desc')}</p>
              </a>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-primary-50 rounded-xl p-8 text-center">
            <h3 className="text-lg font-bold text-foreground-900 mb-3">{t('final.h2')}</h3>
            <p className="text-foreground-600 mb-6 text-sm">{t('final.desc')}</p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-lg hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              {t('cta.explore')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}